import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { app, pages } from '@microsoft/teams-js'; // Using the imported 'app' and HostClientType for context check
import {
  FluentProvider,
  webLightTheme,
  webDarkTheme,
  teamsHighContrastTheme,
} from '@fluentui/react-components'; // Removed Button as it's not used here
import { Theme } from '@radix-ui/themes';
import toast, { Toaster } from 'react-hot-toast'; // toast is default export
import { ReactFlowProvider } from 'reactflow';

import AccessGate from './components/access/AccessGate';
import ProtectedRoutes from './ProtectedRoutes';
import PublicRoutes from './PublicRoutes';
import DataProvider from './context/DataProvider';
import WebDataProvider from './context/WebDataProvider';
import BotsDataProvider from './context/BotsDataProvider';
import { WebSocketProvider } from './context/WebSocketProvider';
import './App.css';
import { BotSessionsProvider } from './context/BotSessionsProvider';
import { WorkflowExecutionsProvider } from './context/WorkflowExecutionsProvider';
import { EmailsProvider } from './context/EmailsProvider';
import { useTeams } from './context/TeamsProvider';
import { McpProvider } from './context/McpProvider';
import { AgentProvider } from './context/AgentProvider';
import { TaskPlanProvider } from './context/TaskPlanProvider';
import {
  ENABLE_AI_CHAT,
  ENABLE_COMMUNICATION,
  ENABLE_RIGHT_SIDEBAR_COMMUNICATION,
  ENABLE_WORKFLOWS,
} from './config/features';
import { NotificationProvider } from './context/NotificationProvider';
import { ChangeRequestProvider } from './context/ChangeRequestProvider';
import { ResourceAllocationProvider } from './context/ResourceAllocationProvider';
import DebugOverlay from './components/DebugOverlay';
import SecurityGate from './components/SecurityGate';
import { useDarkMode } from './hooks/useDarkMode';
import { TEAMS_APP_ID } from './config';

/**
 * Conditionally wraps children in Provider if condition is true.
 * If false, renders children directly — no extra DOM node, no context overhead.
 */
const ConditionalProvider = ({ condition, Provider, children }) =>
  condition ? <Provider>{children}</Provider> : children;

const AppContent = () => {
  useDarkMode();
  // Renamed App to AppContent to use hooks inside Router context
  const [teamsTheme, setTeamsTheme] = useState(webLightTheme);
  const { isTeamsInitialized, setIsTeamsInitialized } = useTeams();

  const [userIdToken, setUserIdToken] = useState(() =>
    localStorage.getItem('userToken'),
  );
  const [projectIdToken, setProjectIdToken] = useState(() =>
    localStorage.getItem('projectToken'),
  );
  const navigate = useNavigate();
  const location = useLocation();

  // First enabled tab in order: chat → communication → workflow → /home
  const defaultRoute = ENABLE_AI_CHAT
    ? '/chat'
    : ENABLE_COMMUNICATION
      ? '/communication'
      : ENABLE_WORKFLOWS
        ? '/workflow'
        : '/home';

  const onLogin = (
    user_id_token,
    received_user_id,
    project_id_token = null,
  ) => {
    // Assuming onLogin might receive user_id too
    setUserIdToken(user_id_token);
    localStorage.setItem('userToken', user_id_token);
    // If user_id is also important for app state beyond JWT, handle it here
    // navigate("/projects");
    // --- NEW LOGIC: Check for project_token to determine navigation ---
    if (project_id_token) {
      // This is a new signup. Set the project token and navigate directly to the chat page.
      setProjectIdToken(project_id_token);
      localStorage.setItem('projectToken', project_id_token);
    }

    const targetPageId = project_id_token ? 'chat' : 'projects';
    const targetEndpoint = project_id_token ? '/chat' : '/projects';

    if (isTeamsInitialized && TEAMS_APP_ID) {
      // Attempt to get current context to see if we're already on the target tab
      app
        .getContext()
        .then((context) => {
          const currentTabEntityId = context.page.id;
          if (currentTabEntityId === targetPageId) {
            // Already on the target tab, force a refresh
            navigate(targetEndpoint);
          } else {
            pages
              .navigateToApp({
                appId: TEAMS_APP_ID,
                pageId: targetPageId,
              })
              .then(() => {
                toast.success(`Navigated to ${targetEndpoint} successfully!`);
              })
              .catch((error) => {
                toast.error(
                  `Error navigating to ${targetEndpoint}: ${error.message}`,
                );
              });
          }
        })
        .catch((error) => {
          // If getContext fails, fallback to just trying to navigate
          pages
            .navigateToApp({
              appId: TEAMS_APP_ID,
              pageId: targetPageId,
            })
            .then(() => {
              toast.success(`Navigated to ${targetEndpoint} successfully!`);
            })
            .catch((navError) => {
              toast.error(
                `Error navigating to ${targetEndpoint}: ${navError.message}`,
              );
            });
        });
    } else {
      // Optional: If already on /projects in browser, force re-render or use other state logic
      if (location.pathname === targetEndpoint) {
        navigate(targetEndpoint, {
          replace: true,
          state: { timestamp: Date.now() },
        }); // Helps trigger re-render in some cases
      } else {
        navigate(targetEndpoint);
      }
    }
  };

  const onProjectAccess = (project_id_token) => {
    setProjectIdToken(project_id_token);
    localStorage.setItem('projectToken', project_id_token);
    // navigate("/chat");
    const targetPageId = 'chat';
    // const targetSubPageId = "generalChannel"; // Optional

    if (isTeamsInitialized) {
      if (pages.currentApp.isSupported()) {
        pages.currentApp
          .navigateTo({
            pageId: targetPageId,
            // subPageId: targetSubPageId,
          })
          .then(() => {
            toast.success('Navigated to chat successfully!');
          })
          .catch((error) => {
            toast.error(`Error navigating to chat: ${error.message}`);
          });
      } else if (TEAMS_APP_ID) {
        pages
          .navigateToApp({
            appId: TEAMS_APP_ID,
            pageId: targetPageId,
            // subPageId: targetSubPageId,
          })
          .then(() => {
            toast.success('Navigated to chat successfully!');
          })
          .catch((error) => {
            toast.error(`Error navigating to chat: ${error.message}`);
          });
      } else {
        navigate(defaultRoute);
      }
    } else {
      navigate(defaultRoute);
    }
  };

  const handleStorageChange = () => {
    // This might be for cross-tab sync, ensure it's used if needed
    setUserIdToken(localStorage.getItem('userToken'));
    setProjectIdToken(localStorage.getItem('projectToken'));
  };

  useEffect(() => {
    app
      .initialize()
      .then(() => {
        setIsTeamsInitialized(true);

        // Notify Teams that your app's main content has loaded.
        app.notifyAppLoaded(); // Use the imported 'app'

        app
          .getContext()
          .then((context) => {
            // Use the imported 'app'
            if (context && context.theme) {
              switch (context.theme) {
                case 'dark':
                  setTeamsTheme(webDarkTheme);
                  break;
                case 'contrast':
                  setTeamsTheme(teamsHighContrastTheme);
                  break;
                default:
                  setTeamsTheme(webLightTheme);
                  break;
              }
            } else {
              setTeamsTheme(webLightTheme);
            }
          })
          .catch((error) => {
            toast.error(`Error getting Teams context: ${error.message}`);
          });

        app.registerOnThemeChangeHandler((theme) => {
          // Use the imported 'app'
          switch (theme) {
            case 'dark':
              setTeamsTheme(webDarkTheme);
              break;
            case 'contrast':
              setTeamsTheme(teamsHighContrastTheme);
              break;
            default:
              setTeamsTheme(webLightTheme);
              break;
          }
        });
      })
      .catch((error) => {
        setIsTeamsInitialized(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array

  // Optional: Loading state while Teams SDK initializes, if you want to prevent rendering LoginDemo too soon.
  //   if (window.microsoftTeams && !isTeamsInitialized) { // Only show loading if trying to init Teams
  //    return <div>Initializing Teams App integration...</div>;
  //   }

  const routeProps = {
    userIdToken,
    projectIdToken,
    onLogin,
    onProjectAccess,
    handleStorageChange,
    isTeamsInitialized,
  };

  return (
    <FluentProvider theme={teamsTheme}>
      <Theme>
        <Toaster
          position="top-right"
          toastOptions={{
            // Default style applied to ALL toasts (plain toast() calls)
            style: {
              background: '#18181b',
              color: '#d4d4d8',
              border: '1px solid #3f3f46',
              borderRadius: '0.625rem',
              padding: '0.75rem 1rem',
              fontSize: '0.875rem',
              boxShadow:
                '0 4px 6px -1px rgba(0,0,0,0.4), 0 2px 4px -1px rgba(0,0,0,0.3)',
              maxWidth: '360px',
            },
            duration: 3000,
            success: {
              style: {
                background: '#18181b',
                color: '#d4d4d8',
                border: '1px solid #3f3f46',
                borderRadius: '0.625rem',
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                boxShadow:
                  '0 4px 6px -1px rgba(0,0,0,0.4), 0 2px 4px -1px rgba(0,0,0,0.3)',
                maxWidth: '360px',
              },
              iconTheme: {
                primary: '#22c55e',
                secondary: '#18181b',
              },
            },
            error: {
              icon: '❌',
              style: {
                background: '#18181b',
                color: '#d4d4d8',
                border: '1px solid #3f3f46',
                borderRadius: '0.625rem',
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                boxShadow:
                  '0 4px 6px -1px rgba(0,0,0,0.4), 0 2px 4px -1px rgba(0,0,0,0.3)',
                maxWidth: '360px',
              },
              iconTheme: {
                primary: '#ef4444',
                secondary: '#18181b',
              },
            },
          }}
        />
        <SecurityGate>
          {userIdToken && projectIdToken ? (
            <AccessGate token={projectIdToken}>
              <WebSocketProvider userGoogleId={projectIdToken}>
                <DataProvider key={`${projectIdToken}-${userIdToken}`}>
                  <WebDataProvider>
                    <ConditionalProvider
                      condition={ENABLE_WORKFLOWS}
                      Provider={BotsDataProvider}
                    >
                      <ConditionalProvider
                        condition={ENABLE_WORKFLOWS}
                        Provider={BotSessionsProvider}
                      >
                        {/* ConditionalProvider only mounts context when its flag is true —
                          no nested ternaries, no duplicated provider trees */}
                        <ConditionalProvider
                          condition={ENABLE_WORKFLOWS}
                          Provider={WorkflowExecutionsProvider}
                        >
                          <ReactFlowProvider>
                            <ConditionalProvider
                              condition={
                                ENABLE_COMMUNICATION ||
                                ENABLE_RIGHT_SIDEBAR_COMMUNICATION
                              }
                              Provider={EmailsProvider}
                            >
                              <ConditionalProvider
                                condition={true}
                                Provider={McpProvider}
                              >
                                <ConditionalProvider
                                  condition={ENABLE_AI_CHAT}
                                  Provider={AgentProvider}
                                >
                                  <ConditionalProvider
                                    condition={ENABLE_AI_CHAT}
                                    Provider={TaskPlanProvider}
                                  >
                                    <ConditionalProvider
                                      // Notifications back every "Chat with Agent" entry
                                      // point (mail, meetings, CRs, deliverables) via
                                      // NotificationProvider.createNewTaskForNotification
                                      // — none of that is workflow-specific, so this must
                                      // not be gated on ENABLE_WORKFLOWS. When it was,
                                      // useNotifications() silently fell back to the
                                      // context's no-op stub with zero errors: chips
                                      // staged fine, but sending them did nothing at all.
                                      condition={true}
                                      Provider={NotificationProvider}
                                    >
                                      <ChangeRequestProvider>
                                        <ResourceAllocationProvider>
                                          <ProtectedRoutes {...routeProps} />
                                        </ResourceAllocationProvider>
                                      </ChangeRequestProvider>
                                    </ConditionalProvider>
                                  </ConditionalProvider>
                                </ConditionalProvider>
                              </ConditionalProvider>
                            </ConditionalProvider>
                          </ReactFlowProvider>
                        </ConditionalProvider>
                      </ConditionalProvider>
                    </ConditionalProvider>
                  </WebDataProvider>
                </DataProvider>
              </WebSocketProvider>
            </AccessGate>
          ) : (
            <PublicRoutes
              key={`${projectIdToken}-${userIdToken}`}
              {...routeProps}
            />
          )}
          <DebugOverlay />
        </SecurityGate>
      </Theme>
    </FluentProvider>
  );
};

const App = () => {
  return <AppContent />;
};

export default App;
